import * as React from 'react';
import { injectIntl, WrappedComponentProps as InjectedIntlProps } from 'react-intl';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { Loader, Modal } from 'semantic-ui-react';

import { getAgreements, setAgreementClosed, setAgreementOpen } from '../../actions/AgreementsActions';
import { StateType } from '../../reducers';
import { agreementsIsFetching, isAgreementOpen } from '../../reducers/AgreementsReducer/AgreementsSelector';
import { formatMessageHelper } from '../../shared/helpers/internationalization';
import analytics from '../../shared/services/analytics/analytics';
import { DispatchConnect, StringTMap } from '../../types';

import './Agreements.scss';
import { AgreementsMain } from './AgreementsMain';
import { displayAgreementContent } from './AgreementsHelpers';

interface AgreementsProps {
  banner?: string;
  othersFetching?: boolean;
  modifiers?: string[];
  info?: () => JSX.Element | JSX.Element[];
  closeModal: () => void;
  handleAccepted: () => void;
  handleRejected: () => void;
  handleClose?: () => void;
  agreementsId: string;
  agreementType: 'agreement' | 'team' | 'competition';
  agreementsAccepted?: boolean;
  agreementsList?: Object[];
  shouldTriggerModalClose?: boolean;
  shouldTriggerModalOnAccepted?: boolean;
  shouldTriggerModalOnRejected?: boolean;
  handleChangeTerms?: (data: any) => void;
  isValidToAgree?: boolean;
  shouldForceHideAgreeButton?: boolean;
  shouldShowAgreeButton?: boolean;
  isEnrollModal?: boolean;
  competitionName?: string;
  competitionId?: string;
  buttonAcceptedText?: string;
  buttonCloseText?: string;
  modalIsOpen?: boolean;
  modalBtnText?: string;
  modalBtnClasses?: string;
  modalClass?: string;
  modalCloseIcon?: boolean;
  modalBtnDisabled?: boolean;
}

interface AgreementsStateProps {
  agreements: any;
  isFetching: boolean;
  isModalOpen: boolean;
}

interface AgreementsState {
  agreementsListValues: StringTMap<boolean>;
}

interface AgreementsStateStateeDispatchers {
  dispatchGetAgreements: (id: string, agreementType: 'agreement' | 'team' | 'competition') => void;
  dispatchOpenAgreement: (id: string) => void;
  dispatchCloseAgreement: (id: string) => void;
}

type AgreementsPropsGeneric = AgreementsProps & AgreementsStateProps & AgreementsStateStateeDispatchers & InjectedIntlProps;

class Agreements extends React.Component<AgreementsPropsGeneric, AgreementsState> {
  constructor(props: AgreementsPropsGeneric) {
    super(props);
    const { modalIsOpen = false } = this.props;
    if (modalIsOpen) {
      this.props.dispatchOpenAgreement(this.props.agreementsId);
    }

    const agreementsListValues = this.setAgreementsListValues();
    this.state = { agreementsListValues };
  }

  setAgreementsListValues() {
    const agreementsListValues: any = {};
    if (this.props.agreementsList !== undefined) {
      this.props.agreementsList.forEach((agreement: any) => {
        agreementsListValues[agreement.name] = agreement.value;
      });
    }

    return agreementsListValues;
  }

  handleCloseModal = () => {
    if (this.props.isEnrollModal && this.props.competitionId) {
      analytics.trackCustomEvent('competition_modal_click_close_icon', { event_label: this.props.competitionId });
    }

    this.props.dispatchCloseAgreement(this.props.agreementsId);

    if (this.props.handleClose && typeof this.props.handleClose === 'function') {
      this.props.handleClose();
    }
  };

  handleOpenModal = (event: any) => {
    event.preventDefault();
    event.stopPropagation();
    this.props.dispatchOpenAgreement(this.props.agreementsId);
  };

  handleAccepted = () => {
    const { shouldTriggerModalOnAccepted, handleAccepted } = this.props;

    if (handleAccepted && typeof handleAccepted === 'function') {
      handleAccepted();
    }

    if (shouldTriggerModalOnAccepted === true) {
      this.handleCloseModal();
    }
  };

  handleRejected = () => {
    const { shouldTriggerModalOnRejected, handleRejected } = this.props;

    if (handleRejected && typeof handleRejected === 'function') {
      handleRejected();
    }

    if (shouldTriggerModalOnRejected === true) {
      this.handleCloseModal();
    }
  };

  handleClose = () => {
    const { shouldTriggerModalClose, handleClose } = this.props;
    if (handleClose && typeof handleClose === 'function') {
      handleClose();
    }

    if (shouldTriggerModalClose === true) {
      this.handleCloseModal();
    }
  };

  checkAllAgreementsAccepted = (): boolean => {
    if (this.props.isValidToAgree === false) {
      return this.props.isValidToAgree;
    }
    let allAccepted = true;
    const values = this.state.agreementsListValues;

    Object.keys(values).forEach((key: string) => {
      if (allAccepted === false) {
        return;
      }

      allAccepted = values[key];
    });

    return allAccepted;
  };

  handleChangeAgreements = (_: any, data: any) => {
    const name = data.name;
    const value = data.checked;
    this.setState({ agreementsListValues: { ...this.state.agreementsListValues, [name]: value } }, () => {
      if (this.props.handleChangeTerms) {
        this.props.handleChangeTerms(data);
      }
    });
  };

  setHandleOnClose = () => {
    if (this.props.handleClose && typeof this.props.handleClose === 'function') {
      return this.handleClose;
    } else if (this.props.shouldTriggerModalClose === true) {
      return this.handleCloseModal;
    } else {
      return undefined;
    }
  };

  setHandleAccepted = () => {
    if (this.props.handleAccepted && typeof this.props.handleAccepted === 'function') {
      return this.handleAccepted;
    } else if (this.props.shouldTriggerModalOnAccepted === true) {
      return this.handleCloseModal;
    } else {
      return undefined;
    }
  };

  setHandleRejected = () => {
    if (this.props.handleRejected && typeof this.props.handleRejected === 'function') {
      return this.handleRejected;
    } else if (this.props.shouldTriggerModalOnRejected === true) {
      return this.handleCloseModal;
    } else {
      return undefined;
    }
  };

  setTriggerModal = () => {
    const { modalBtnText = '', modalBtnClasses = 'link', modalBtnDisabled = false, intl, modalIsOpen } = this.props;

    if (!modalIsOpen && modalBtnText !== '') {
      return (
        <button type="button" onClick={this.handleOpenModal} className={modalBtnClasses} disabled={modalBtnDisabled}>
          {formatMessageHelper(intl, modalBtnText)}
        </button>
      );
    } else {
      return null;
    }
  };

  componentDidUpdate(prevPros: AgreementsPropsGeneric) {
    if (this.props.agreementsId !== prevPros.agreementsId) {
      this.props.dispatchGetAgreements(this.props.agreementsId, this.props.agreementType);
      this.setState({ agreementsListValues: this.setAgreementsListValues() });

      // If modal should be open, ensure the new agreement ID is added to openAgreements
      if (this.props.modalIsOpen) {
        this.props.dispatchOpenAgreement(this.props.agreementsId);
      }
    }
  }

  getAgreements = () => {
    this.props.dispatchGetAgreements(this.props.agreementsId, this.props.agreementType);
    this.setState({ agreementsListValues: this.setAgreementsListValues() });
  };

  render() {
    const {
      shouldShowAgreeButton = false,
      modalCloseIcon = true,
      othersFetching = false,
      shouldForceHideAgreeButton = false,
      agreementsList,
      agreements,
    } = this.props;

    const isAgreementButtonVisible =
      !shouldForceHideAgreeButton &&
      ((agreementsList && this.state.agreementsListValues && agreements && displayAgreementContent(agreements)) || shouldShowAgreeButton);
    return (
      <Modal
        size="large"
        trigger={this.setTriggerModal()}
        closeIcon={modalCloseIcon}
        closeOnDimmerClick={false}
        closeOnEscape={false}
        open={this.props.isModalOpen}
        onClose={this.handleCloseModal}
        onMount={this.getAgreements}
        className={this.props.modalClass}
      >
        <Modal.Content>
          {this.props.isFetching || othersFetching ? (
            <div className="loader">
              <Loader active={true} inline="centered" size="massive" />
            </div>
          ) : (
            <AgreementsMain
              info={this.props.info}
              modifiers={this.props.modifiers === undefined ? [] : this.props.modifiers}
              banner={this.props.banner}
              agreements={this.props.agreements}
              buttonAcceptedText={this.props.buttonAcceptedText}
              buttonCloseText={this.props.buttonCloseText}
              handleClose={this.setHandleOnClose()}
              handleLater={this.handleCloseModal}
              handleAccepted={this.setHandleAccepted()}
              handleReject={this.setHandleRejected()}
              isEnrollModal={this.props.isEnrollModal}
              competitionName={this.props.competitionName}
              competitionId={this.props.competitionId}
              agreementsList={this.props.agreementsList}
              handleChangeAgreements={this.handleChangeAgreements}
              agreementsListValues={this.props.agreementsList ? this.state.agreementsListValues : undefined}
              agreementsAccepted={
                this.props.agreementsList ? this.checkAllAgreementsAccepted() : shouldShowAgreeButton === true ? true : undefined
              }
              isAgreementButtonVisible={isAgreementButtonVisible}
            />
          )}
        </Modal.Content>
      </Modal>
    );
  }
}

const mapStateToProps = (state: StateType, ownProps: AgreementsPropsGeneric): AgreementsStateProps => ({
  agreements: state.getIn(['agreements', 'agreements', ownProps.agreementsId]),
  // @ts-ignore
  isFetching: agreementsIsFetching(state),
  isModalOpen: isAgreementOpen(state, ownProps.agreementsId),
});

const mapDispatchToProps = (dispatch: Dispatch<any>): AgreementsStateStateeDispatchers => ({
  dispatchGetAgreements: (id: string, agreementType: 'agreement' | 'team' | 'competition') => {
    dispatch(getAgreements(id, agreementType));
  },
  dispatchOpenAgreement: (id: string) => {
    dispatch(setAgreementOpen({ id }));
  },
  dispatchCloseAgreement: (id: string) => {
    dispatch(setAgreementClosed({ id }));
  },
});

export default connect<AgreementsStateProps, DispatchConnect<AgreementsStateStateeDispatchers>, any, StateType>(
  mapStateToProps,
  mapDispatchToProps
)(injectIntl(Agreements));
